import { useState } from 'react'
import { MenuItem, TextField, Typography } from '@mui/material'
import type {
  Partnership,
  PointSolutionGroup,
  PolicyPointSolution,
  PolicyPointSolutionAction,
} from './BenefitPolicyService'
import { RecordDialog } from './RecordDialog'

interface PointSolutionDialogProps {
  // The attachment being edited; absent means this is an add into `defaultGroup`.
  pointSolution?: PolicyPointSolution
  /** The group an add lands in — the one whose button was pressed. */
  defaultGroup: PointSolutionGroup
  partnerships: Partnership[]
  /** The groups to choose from, from the lookups endpoint — this screen names none of its own. */
  groupOptions: { value: string; display_name: string }[]
  isActive: boolean
  saving: boolean
  refusal?: string
  onClose: () => void
  onConfirm: (action: PolicyPointSolutionAction) => void
}

/**
 * Attach a partnership to this plan, or change how one already attached is presented.
 *
 * The partner itself is fixed once attached: swapping it would leave the row carrying a link
 * written for the partner it replaced, which the endpoint refuses. The group and the link are not
 * fixed — a partner moves between Firefly and employer funding while the link stays valid.
 */
export const PointSolutionDialog = ({
  pointSolution,
  defaultGroup,
  partnerships,
  groupOptions,
  isActive,
  saving,
  refusal,
  onClose,
  onConfirm,
}: PointSolutionDialogProps) => {
  const [partnership, setPartnership] = useState<number | ''>(pointSolution?.partnership ?? '')
  const [group, setGroup] = useState<PointSolutionGroup>(
    pointSolution?.point_solution_type ?? defaultGroup,
  )
  const [url, setUrl] = useState(pointSolution?.point_solution_url ?? '')

  const isEdit = pointSolution !== undefined

  const confirm = () => {
    // Both content fields travel, `null` included: the endpoint refuses a write that leaves one
    // out, because an omission cannot be told apart from a deliberate clear.
    const content = { point_solution_type: group, point_solution_url: url.trim() || null }
    if (pointSolution) {
      onConfirm({ action: 'update', point_solution: pointSolution.id, ...content })
    } else if (partnership !== '') {
      onConfirm({ action: 'add', partnership, ...content })
    }
  }

  return (
    <RecordDialog
      title={isEdit ? `Edit ${pointSolution.partner_name ?? 'partner'}` : 'Add a partner'}
      confirmLabel={isEdit ? 'Save' : 'Add partner'}
      busy={saving}
      incomplete={!isEdit && partnership === ''}
      notice={
        isActive
          ? 'This policy is live. Members see this under Additional benefits as soon as you save.'
          : undefined
      }
      refusal={refusal}
      testId="point-solution"
      maxWidth="xs"
      onClose={onClose}
      onConfirm={confirm}
    >
      {isEdit ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          To offer a different partner, add them and remove this one.
        </Typography>
      ) : (
        <TextField
          select
          required
          fullWidth
          label="Partner"
          value={partnership}
          onChange={event => setPartnership(Number(event.target.value))}
          data-testid="point-solution-partnership"
        >
          {partnerships.map(candidate => (
            <MenuItem key={candidate.id} value={candidate.id}>
              {candidate.partner_name}
              {candidate.is_active ? '' : ' (inactive)'}
            </MenuItem>
          ))}
        </TextField>
      )}

      <TextField
        select
        fullWidth
        label="Funded by"
        value={group}
        onChange={event => setGroup(event.target.value as PointSolutionGroup)}
        helperText="Which group the member sees this under."
        data-testid="point-solution-group"
      >
        {groupOptions.map(option => (
          <MenuItem key={option.value} value={option.value}>
            {option.display_name}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        fullWidth
        label="Link"
        value={url}
        onChange={event => setUrl(event.target.value)}
        helperText="Where the member goes to get started. Optional."
        data-testid="point-solution-url"
      />
    </RecordDialog>
  )
}
